import {
	DESTINATION_TYPE_TABLE,
	POI_DOT_COLOR_BY_FILL,
	POI_FULL_MARKER_ZOOM,
	POI_HIDDEN_BELOW_ZOOM,
	POI_NAME_PLATE_ZOOM,
	STOP_PHOTO_IN_ZOOM,
	STOP_PHOTO_OUT_ZOOM,
} from "components/map/markerVisualTokens";
import { curvedOrStraightLine, type LonLat } from "utils/curveLine";
import type { PoiGroup, PoiMarkerShape } from "../shared/mapModel";
import { HOSTEL_SLUGS, HOSTELS, type HostelKind, type Poi } from "./content";
import { inRoute, type RouteStop } from "./plannerLogic";

/**
 * What the hostel map shows, as plain data: which hostels a filter puts on the map, the pink line through a
 * route, the camera frame and the zoom ladder. The live map (MadMonkeyMap) only draws what this decides.
 */

export const FILTER_ROUTE = "route";
export const FILTER_ALL = "all";
/** "route", "all", or a country code from COUNTRIES */
export type MapFilter = string;

/**
 * Which hostels a filter shows, and which of them the camera frames.
 * - "Your route": the chosen stops (every hostel while the route is empty, so the map is never bare).
 * - "All": the 23.
 * - A country: its hostels, plus the route's stops from elsewhere so the line always runs between markers;
 *   the camera frames the country only, those stops sit off-frame.
 */
export function visibleHostels(filter: MapFilter, route: RouteStop[]): { shown: string[]; fit: string[] } {
	if (filter === FILTER_ROUTE && route.length) {
		const stops = route.map((r) => r.s);
		return { shown: stops, fit: stops };
	}
	if (filter === FILTER_ROUTE || filter === FILTER_ALL) return { shown: HOSTEL_SLUGS, fit: HOSTEL_SLUGS };
	const inCountry = HOSTEL_SLUGS.filter((s) => HOSTELS[s].cc === filter);
	return { shown: HOSTEL_SLUGS.filter((s) => inCountry.includes(s) || inRoute(route, s)), fit: inCountry };
}

export const hostelPoint = (slug: string): LonLat => [HOSTELS[slug].lon, HOSTELS[slug].lat];

/** The pink line through the stops in order, one curved leg per pair (a lone leg stays straight). */
export function routeLine(slugs: string[]): GeoJSON.FeatureCollection<GeoJSON.LineString> {
	const points = slugs.map(hostelPoint);
	const legs = points.length - 1;
	return { type: "FeatureCollection", features: points.slice(1).map((end, i) => curvedOrStraightLine(points[i], end, i, legs)) };
}

// ---- the zoom ladder ----------------------------------------------------------------------------------------

/**
 * Below this zoom a hostel is a dot. The app's own map no longer dots its stops (it shows a few); this page
 * shows 23 across a region at once, so it keeps the original ladder's first rung (design/map-markers/markers.md).
 * That rung was measured on the prototype's maps, 880px wide: on a narrower map the same ground is a lower zoom,
 * so the zoom is first read as if the map were 880px wide.
 */
export const HOSTEL_DOT_BELOW_ZOOM = 5.2;
const REFERENCE_MAP_WIDTH = 880;

export const hostelIsDot = (zoom: number, mapWidth: number, force = false): boolean =>
	!force && zoom + Math.log2(REFERENCE_MAP_WIDTH / Math.max(mapWidth, 1)) < HOSTEL_DOT_BELOW_ZOOM;

/** A marker may carry its photo card only inside the app's own window for stops (collisions thin it further). */
export const hostelMayShowPhoto = (zoom: number): boolean => zoom >= STOP_PHOTO_IN_ZOOM && zoom < STOP_PHOTO_OUT_ZOOM;

export type PoiTier = "hidden" | "dot" | "marker";

/** `always`: the hostel page's own map, where the places are the point and show in full at any zoom. */
export function poiTier(zoom: number, always: boolean): PoiTier {
	if (always || zoom >= POI_FULL_MARKER_ZOOM) return "marker";
	return zoom >= POI_HIDDEN_BELOW_ZOOM ? "dot" : "hidden";
}

export const poiMayShowName = (zoom: number, always: boolean): boolean => always || zoom >= POI_NAME_PLATE_ZOOM;

// ---- colours ------------------------------------------------------------------------------------------------

// The app's own marker colours (markerVisualTokens): a hostel is tinted like a city, town or island (a nearby place
// like the app's things to do, food and drink, outdoors: shared/mapModel). A dot is the saturated version of the tint.
export const HOSTEL_FILL: Record<HostelKind | "stay", string> = {
	stay: DESTINATION_TYPE_TABLE.city.fill,
	city: DESTINATION_TYPE_TABLE.city.fill,
	town: DESTINATION_TYPE_TABLE.town.fill,
	island: DESTINATION_TYPE_TABLE["island-beach"].fill,
};

export const HOSTEL_DOT: Record<HostelKind | "stay", string> = {
	stay: POI_DOT_COLOR_BY_FILL[HOSTEL_FILL.stay],
	city: POI_DOT_COLOR_BY_FILL[HOSTEL_FILL.city],
	town: POI_DOT_COLOR_BY_FILL[HOSTEL_FILL.town],
	island: POI_DOT_COLOR_BY_FILL[HOSTEL_FILL.island],
};

export const POI_GROUP_LABEL: Record<PoiGroup, string> = { do: "Things to do", food: "Eat & drink", out: "Outdoors" };

/** a nearby place's distance from its hostel: "850 m", "1.7 km" */
export const fmtKm = (d: number): string => (d < 1 ? `${Math.round((d * 1000) / 50) * 50} m` : `${d.toFixed(1).replace(".0", "")} km`);

// ---- markers ------------------------------------------------------------------------------------------------

export interface HostelMarkerData {
	type: "hostel";
	/** hostel slug */
	id: string;
	lat: number;
	lon: number;
	/** what the name plate says */
	name: string;
	photo: string;
	/** colours the marker like the app's city / town / island; "stay" is the plain green of the route builder */
	kind: HostelKind | "stay";
	/** a stop of the visitor's route or of the route this map draws: carries the green check */
	inRoute: boolean;
	active: boolean;
	/** an add button on the marker, for a hostel that is not in the route yet */
	addable: boolean;
	/** a marker that may take a name plate when there is room for it */
	plate: boolean;
	/** always a full marker with its photo card, whatever the zoom or the crowd (the hero's focus stop) */
	force: boolean;
}

export interface PoiMarkerData extends PoiMarkerShape {
	type: "poi";
	/** `<hostel slug>:<index in its pois>`, unique across hostels */
	id: string;
	hostel: string;
	/** index in the hostel's `pois` */
	index: number;
	lat: number;
	lon: number;
}

export type MapMarkerData = HostelMarkerData | PoiMarkerData;

export function hostelMarker(slug: string, opts: Partial<Omit<HostelMarkerData, "type" | "id">> = {}): HostelMarkerData {
	const h = HOSTELS[slug];
	return {
		type: "hostel",
		id: slug,
		lat: h.lat,
		lon: h.lon,
		name: h.name,
		photo: h.photos[0],
		kind: h.kind,
		inRoute: false,
		active: false,
		addable: false,
		plate: true,
		force: false,
		...opts,
	};
}

/** `pois`: the hostel's static places unless a page has others for it (its synced list, `livePois.ts`). */
export function poiMarkers(slug: string, activeIndex = -1, pois: Poi[] = HOSTELS[slug].pois ?? []): PoiMarkerData[] {
	return pois.map((p, i) => ({
		type: "poi",
		id: `${slug}:${i}`,
		hostel: slug,
		index: i,
		lat: p.lat,
		lon: p.lon,
		name: p.name,
		grp: p.grp,
		icon: p.icon,
		photo: p.photo,
		active: i === activeIndex,
	}));
}
